import type { ReactNode } from 'react';

/**
 * A tiny representative sketch of each form, for the chart-type picker.
 *
 * These are schematic, not data-driven — a recognisable silhouette of the
 * shape a form draws, so an author browsing the gallery reads it by picture as
 * well as by name. Marks use `currentColor` (the picker sets it to the brand),
 * so a thumbnail themes with the card and follows light/dark for free.
 */

const wrap = (children: ReactNode) => (
  <svg viewBox="0 0 80 44" className="tpl-thumb__svg" aria-hidden="true" focusable="false">
    {children}
  </svg>
);

const c = 'currentColor';

const THUMBS: Record<string, ReactNode> = {
  bar: wrap(
    <>
      <rect x="4" y="6" width="40" height="6" rx="2" fill={c} />
      <rect x="4" y="19" width="64" height="6" rx="2" fill={c} />
      <rect x="4" y="32" width="28" height="6" rx="2" fill={c} />
    </>,
  ),
  column: wrap(
    <>
      <rect x="8" y="22" width="10" height="18" rx="2" fill={c} />
      <rect x="26" y="10" width="10" height="30" rx="2" fill={c} />
      <rect x="44" y="26" width="10" height="14" rx="2" fill={c} />
      <rect x="62" y="16" width="10" height="24" rx="2" fill={c} />
    </>,
  ),
  grouped: wrap(
    <>
      <rect x="8" y="16" width="7" height="24" rx="1.5" fill={c} />
      <rect x="16" y="24" width="7" height="16" rx="1.5" fill={c} opacity="0.5" />
      <rect x="34" y="10" width="7" height="30" rx="1.5" fill={c} />
      <rect x="42" y="20" width="7" height="20" rx="1.5" fill={c} opacity="0.5" />
      <rect x="60" y="22" width="7" height="18" rx="1.5" fill={c} />
      <rect x="68" y="28" width="7" height="12" rx="1.5" fill={c} opacity="0.5" />
    </>,
  ),
  leaderboard: wrap(
    <>
      <rect x="4" y="7" width="52" height="7" rx="2" fill={c} />
      <rect x="4" y="18" width="40" height="7" rx="2" fill={c} opacity="0.7" />
      <rect x="4" y="29" width="30" height="7" rx="2" fill={c} opacity="0.5" />
    </>,
  ),
  'ranked-columns': wrap(
    <>
      <rect x="6" y="6" width="18" height="4" rx="2" fill={c} />
      <rect x="6" y="14" width="18" height="4" rx="2" fill={c} opacity="0.6" />
      <rect x="6" y="22" width="18" height="4" rx="2" fill={c} opacity="0.4" />
      <rect x="31" y="6" width="18" height="4" rx="2" fill={c} />
      <rect x="31" y="14" width="18" height="4" rx="2" fill={c} opacity="0.6" />
      <rect x="56" y="6" width="18" height="4" rx="2" fill={c} />
      <rect x="56" y="14" width="18" height="4" rx="2" fill={c} opacity="0.6" />
    </>,
  ),
  stat: wrap(
    <>
      <rect x="10" y="10" width="30" height="5" rx="2" fill={c} opacity="0.5" />
      <rect x="10" y="19" width="46" height="12" rx="2" fill={c} />
    </>,
  ),
  trend: wrap(
    <>
      <polygon points="6,34 24,20 42,26 60,12 74,20 74,40 6,40" fill={c} opacity="0.16" />
      <polyline points="6,34 24,20 42,26 60,12 74,20" fill="none" stroke={c} strokeWidth="2" />
    </>,
  ),
  line: wrap(
    <polyline
      points="6,34 22,22 38,28 54,12 72,18"
      fill="none"
      stroke={c}
      strokeWidth="2"
      strokeLinecap="round"
    />,
  ),
  spline: wrap(
    <path
      d="M6 32 C18 32 18 16 32 16 C46 16 44 12 58 12 C68 12 70 20 74 18"
      fill="none"
      stroke={c}
      strokeWidth="2"
    />,
  ),
  area: wrap(
    <>
      <polygon points="6,34 24,20 42,26 60,12 74,20 74,40 6,40" fill={c} opacity="0.2" />
      <polyline points="6,34 24,20 42,26 60,12 74,20" fill="none" stroke={c} strokeWidth="2" />
    </>,
  ),
  stacked: wrap(
    <>
      <rect x="12" y="24" width="12" height="16" rx="1" fill={c} />
      <rect x="12" y="14" width="12" height="9" rx="1" fill={c} opacity="0.5" />
      <rect x="34" y="20" width="12" height="20" rx="1" fill={c} />
      <rect x="34" y="10" width="12" height="9" rx="1" fill={c} opacity="0.5" />
      <rect x="56" y="26" width="12" height="14" rx="1" fill={c} />
      <rect x="56" y="16" width="12" height="9" rx="1" fill={c} opacity="0.5" />
    </>,
  ),
  pie: wrap(
    <>
      <path d="M40 22 L40 6 A16 16 0 0 1 54 30 Z" fill={c} />
      <path d="M40 22 L54 30 A16 16 0 0 1 26 30 Z" fill={c} opacity="0.55" />
      <path d="M40 22 L26 30 A16 16 0 0 1 40 6 Z" fill={c} opacity="0.3" />
    </>,
  ),
  donut: wrap(
    <>
      <circle cx="40" cy="22" r="15" fill="none" stroke={c} strokeWidth="7" strokeDasharray="55 40" />
      <circle cx="40" cy="22" r="15" fill="none" stroke={c} strokeWidth="7" opacity="0.25" />
    </>,
  ),
  treemap: wrap(
    <>
      <rect x="5" y="6" width="34" height="32" fill={c} opacity="0.7" />
      <rect x="41" y="6" width="34" height="17" fill={c} opacity="0.4" />
      <rect x="41" y="25" width="16" height="13" fill={c} opacity="0.55" />
      <rect x="59" y="25" width="16" height="13" fill={c} opacity="0.25" />
    </>,
  ),
  scatter: wrap(
    <>
      <circle cx="14" cy="32" r="3" fill={c} />
      <circle cx="26" cy="24" r="3" fill={c} />
      <circle cx="36" cy="28" r="3" fill={c} />
      <circle cx="48" cy="16" r="3" fill={c} />
      <circle cx="60" cy="20" r="3" fill={c} />
      <circle cx="70" cy="10" r="3" fill={c} />
    </>,
  ),
  bubble: wrap(
    <>
      <circle cx="18" cy="28" r="6" fill={c} opacity="0.5" />
      <circle cx="38" cy="18" r="3.5" fill={c} opacity="0.5" />
      <circle cx="52" cy="28" r="9" fill={c} opacity="0.5" />
      <circle cx="68" cy="14" r="4.5" fill={c} opacity="0.5" />
    </>,
  ),
  heatmap: wrap(
    <>
      {[0, 1, 2].map((row) =>
        [0, 1, 2, 3].map((col) => (
          <rect
            key={`${row}-${col}`}
            x={6 + col * 17}
            y={6 + row * 11}
            width="15"
            height="9"
            fill={c}
            opacity={0.2 + ((row * 4 + col) % 5) * 0.16}
          />
        )),
      )}
    </>,
  ),
  histogram: wrap(
    <>
      <rect x="8" y="30" width="10" height="10" fill={c} />
      <rect x="18" y="22" width="10" height="18" fill={c} />
      <rect x="28" y="12" width="10" height="28" fill={c} />
      <rect x="38" y="18" width="10" height="22" fill={c} />
      <rect x="48" y="26" width="10" height="14" fill={c} />
      <rect x="58" y="32" width="10" height="8" fill={c} />
    </>,
  ),
  gauge: wrap(
    <>
      <path d="M14 36 A26 26 0 0 1 66 36" fill="none" stroke={c} strokeWidth="6" opacity="0.25" />
      <path d="M14 36 A26 26 0 0 1 40 10" fill="none" stroke={c} strokeWidth="6" strokeLinecap="round" />
      <line x1="40" y1="36" x2="54" y2="20" stroke={c} strokeWidth="2.5" />
    </>,
  ),
  radar: wrap(
    <>
      <polygon points="40,6 66,22 56,38 24,38 14,22" fill="none" stroke={c} strokeWidth="1" opacity="0.35" />
      <polygon points="40,14 58,24 50,34 30,32 24,22" fill={c} opacity="0.2" stroke={c} strokeWidth="1.5" />
    </>,
  ),
  waterfall: wrap(
    <>
      <rect x="8" y="26" width="12" height="12" fill={c} />
      <rect x="24" y="16" width="12" height="10" fill={c} opacity="0.8" />
      <rect x="40" y="20" width="12" height="8" fill={c} opacity="0.55" />
      <rect x="56" y="10" width="12" height="18" fill={c} opacity="0.35" />
    </>,
  ),
  'step-line': wrap(
    <polyline
      points="6,34 24,34 24,22 42,22 42,28 60,28 60,12 74,12"
      fill="none"
      stroke={c}
      strokeWidth="2"
      strokeLinecap="round"
    />,
  ),
  'stacked-area': wrap(
    <>
      <polygon points="6,40 24,30 42,34 60,26 74,30 74,40 6,40" fill={c} opacity="0.35" />
      <polygon points="6,30 24,22 42,26 60,16 74,22 74,30 60,26 42,34 24,30 6,40" fill={c} opacity="0.2" />
    </>,
  ),
  sunburst: wrap(
    <>
      <circle cx="40" cy="22" r="7" fill={c} opacity="0.8" />
      <path d="M40 22 L40 8 A14 14 0 0 1 52 15 Z" fill={c} opacity="0.5" />
      <path d="M40 22 L53 29 A14 14 0 0 1 31 35 Z" fill={c} opacity="0.35" />
      <path d="M40 22 L27 15 A14 14 0 0 1 40 8 Z" fill={c} opacity="0.25" />
    </>,
  ),
  funnel: wrap(
    <>
      <polygon points="12,7 68,7 58,17 22,17" fill={c} />
      <polygon points="24,20 56,20 48,30 32,30" fill={c} opacity="0.6" />
      <polygon points="34,33 46,33 41,40 39,40" fill={c} opacity="0.35" />
    </>,
  ),
  worldmap: wrap(
    <>
      <path d="M8 18 L26 10 L40 16 L36 28 L18 30 Z" fill={c} opacity="0.7" />
      <path d="M42 12 L64 8 L72 22 L54 32 L42 26 Z" fill={c} opacity="0.4" />
    </>,
  ),
  table: wrap(
    <>
      <rect x="6" y="7" width="68" height="7" rx="1" fill={c} opacity="0.5" />
      <rect x="6" y="17" width="68" height="6" rx="1" fill={c} opacity="0.25" />
      <rect x="6" y="26" width="68" height="6" rx="1" fill={c} opacity="0.25" />
      <line x1="29" y1="7" x2="29" y2="32" stroke="var(--surface)" strokeWidth="2" />
      <line x1="52" y1="7" x2="52" y2="32" stroke="var(--surface)" strokeWidth="2" />
    </>,
  ),
};

/** The sketch for a template id, or a neutral placeholder if none is defined. */
export const Thumbnail = ({ id }: { id: string }): JSX.Element => {
  const thumb = THUMBS[id];
  if (thumb) return <span className="tpl-thumb">{thumb}</span>;
  return (
    <span className="tpl-thumb">
      {wrap(<rect x="8" y="10" width="64" height="24" rx="3" fill={c} opacity="0.2" />)}
    </span>
  );
};
